import dateFormat from "dateformat";
import get from "lodash/get";
import React, { useEffect } from "react";
import { Button, Modal, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { isBefore, parse } from "date-fns";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import RatingVersionForm from "~/components/Forms/RatingVersionForm";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import {
  useDeleteQVersionRatingMutation,
  useDeleteVersionRatingMutation,
  useGetGrVersionsLazyQuery,
  useGetQVersionsLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { ConfirmModal } from "~/shared/ui/ConfirmModal";
import { TableV2 } from "~/shared/ui/TableV2";
import { DefaultRowControl } from "~/shared/ui/TableV2/ui/DefaultRowControl";

import { getEditorVersionsColumns } from "./model/tableAdapter";
import { EDITOR_VERSIONS_OPTIONS } from "./options";

type EditorVersionTableRow = {
  id: string | number;
  dateBegin: string;
};

const EditorVersions = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const setData = useSetLocationData();
  const { hasPermission } = usePermissions();
  const { openModal, currentId } = useGetLocationData();
  const goBack = useGoToPreviousPage();
  const [getGrVersions, GrVersions] = useGetGrVersionsLazyQuery({
    fetchPolicy: "cache-and-network",
  });
  const [getQVersions, QVersions] = useGetQVersionsLazyQuery({
    fetchPolicy: "cache-and-network",
  });
  const managing = get(history, "location.pathname", "");
  const GR = managing === "/editor/gr/";
  const Q = managing === "/editor/q/";

  useEffect(() => {
    GR && getGrVersions();
    Q && getQVersions();
  }, [GR, Q]);

  const isEdit =
    (GR &&
      hasPermission("globalRatings.versions.get") &&
      hasPermission("globalRatings.version.set")) ||
    (Q &&
      hasPermission("quartilesRatings.versions.get") &&
      hasPermission("quartilesRatings.version.set"));

  const grVersions = get(GrVersions, "data.GlobalRatingGetter.getVersions", []);
  const grs = grVersions.map(({ dateBegin, ...gr }) => ({
    ...gr,
    dateBegin: dateFormat(dateBegin, "dd.mm.yyyy"),
  }));
  const qVersions = get(
    QVersions,
    "data.QuartilesRatingGetter.getVersions",
    [],
  );
  const qs = qVersions.map(({ dateBegin, ...gr }) => ({
    ...gr,
    dateBegin: dateFormat(dateBegin, "dd.mm.yyyy"),
  }));

  const list = (GR && grVersions) || (Q && qVersions);
  const selectedVersion = Array.isArray(list)
    ? list.find((i) => i.id === currentId)
    : undefined;

  const isOpenAddModal = openModal === "Add";
  const isOpenEditModal = openModal === "Edit";

  const openAddModal = () => {
    setData("openModal", "Add");
  };

  const handleCloseModal = () => {
    setData("openModal", "", "openChildModal", "");
  };

  const [deleteVersionRatingMutation] = useDeleteVersionRatingMutation({
    variables: { id: String(currentId) },
    onCompleted() {
      GrVersions.refetch();
    },
  });

  const [deleteQVersionRatingMutation] = useDeleteQVersionRatingMutation({
    variables: { id: String(currentId) },
    onCompleted() {
      QVersions.refetch();
    },
  });

  const deleteVersionRating = () => {
    GR ? deleteVersionRatingMutation() : deleteQVersionRatingMutation();
    handleCloseModal();
  };

  const typeActive = (GR && "GR") || (Q && "Q");

  const visibleOptions = GR
    ? EDITOR_VERSIONS_OPTIONS.filter((option) => option !== "IndicatorGroupId")
    : EDITOR_VERSIONS_OPTIONS;

  const columns = getEditorVersionsColumns(t, visibleOptions);
  const dataSource = (GR && grs) || (Q && qs) || [];

  const canEditVersion =
    (GR && hasPermission("globalRatings.version.set")) ||
    (Q && hasPermission("quartilesRatings.version.set"));

  const handleVersionRowEdit = (record: { id: string | number }) => {
    setData("openModal", "Edit", "currentId", record.id);
  };

  const handleVersionRowNavigate = (record: {
    id: string | number;
    dateBegin: string;
  }) => {
    history.push(`${record.id}/`);
    setData("dateBegin", record.dateBegin);
  };

  const isVersionRowDeletable = (record: EditorVersionTableRow) =>
    isBefore(new Date(), parse(record.dateBegin, "dd.MM.yyyy", new Date()));

  const handleVersionRowDelete = (record: { id: string | number }) => {
    setData("openModal", "Sure", "currentId", record.id);
  };

  const formId = "RatingVersionForm";

  return (
    <Wrapper.Content
      title={
        (GR && t("editor.managing_gr_version")) ||
        (Q && t("editor.managing_q_version"))
      }
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {isEdit && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2<EditorVersionTableRow>
        columns={columns}
        dataSource={dataSource as EditorVersionTableRow[]}
        loading={GrVersions.loading || QVersions.loading}
        {...(GR || Q
          ? {
              rowActions: (record) => (
                <DefaultRowControl
                  record={record}
                  onRowEdit={canEditVersion ? handleVersionRowEdit : undefined}
                  onRowDelete={
                    isVersionRowDeletable(record)
                      ? handleVersionRowDelete
                      : undefined
                  }
                />
              ),
              rowActionsColumn: { width: 80, minWidth: 52 },
              actionsFixed: true,
              actionsPlacement: "left" as const,
              onRow: (record) => ({
                onClick: () => handleVersionRowNavigate(record),
              }),
            }
          : {})}
      />

      <Modal
        title={
          isOpenAddModal
            ? t(`editor.versions.${GR ? "gr" : "q"}_version_add`)
            : t(`editor.versions.${GR ? "gr" : "q"}_version_edit`)
        }
        open={isOpenAddModal || isOpenEditModal}
        onCancel={handleCloseModal}
        closable
        centered
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
        destroyOnHidden
      >
        {(isOpenAddModal || isOpenEditModal) && (
          <RatingVersionForm
            setCloseModal={handleCloseModal}
            refetch={GR ? GrVersions.refetch : QVersions.refetch}
            ratingVersion={isOpenEditModal ? selectedVersion : undefined}
            type={typeActive}
            formId={formId}
          />
        )}
      </Modal>

      <ConfirmModal
        title={t("indicators.indicator_del")}
        open={openModal === "Sure"}
        onConfirm={deleteVersionRating}
        onCancel={handleCloseModal}
        centered
        closable
        destroyOnHidden
      />
    </Wrapper.Content>
  );
};
export default EditorVersions;
